import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { QuoteRequestPayload } from '@/types'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowRight, ChevronDown, CircleAlert, CircleCheck, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

import {
    CUSTOM_MODEL,
    LAND_STATUSES,
    TIMELINES,
    UNSURE_MODEL,
    USE_CASES,
    VISIT_MESSAGE
} from '@/constants/quote'

export type ModelOption = {
    id: string
    slug: string
    name: string
    price: string | null
    length: string | null
}

interface QuoteFormProps {
    models: ModelOption[]
}

type FieldName = keyof QuoteRequestPayload
type FieldErrors = Partial<Record<FieldName, string>>
type Status = 'idle' | 'submitting' | 'success' | 'error'

const EMPTY_FORM: QuoteRequestPayload = {
    name: '',
    email: '',
    phone: '',
    model: '',
    useCase: '',
    timeline: '',
    landStatus: '',
    message: '',
    website: ''
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const selectClassName = 'h-11 w-full appearance-none rounded-xl border border-input bg-card px-3.5 pr-10 text-base transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 aria-invalid:border-destructive md:text-sm'

const validate = (values: QuoteRequestPayload): FieldErrors => {
    const errors: FieldErrors = {}

    if (values.name.trim().length < 2) errors.name = "Please enter your name."
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = "Please enter a valid email address."
    if (values.message.length > 4000) errors.message = "Please keep your message under 4,000 characters."

    return errors
}

const FieldError: FC<{ id: string, message?: string }> = ({ id, message }): ReactNode => {
    if (!message) return null

    return (
        <p id={id} className='mt-2 flex items-center gap-1.5 text-sm text-destructive'>
            <CircleAlert className='size-4 shrink-0' aria-hidden='true' />
            {message}
        </p>
    )
}

export const QuoteForm: FC<QuoteFormProps> = ({ models }): ReactNode => {
    const router = useRouter()
    const [values, setValues] = useState<QuoteRequestPayload>(EMPTY_FORM)
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')

    useEffect(() => {
        if (!router.isReady) return

        const requested = router.query.model
        const wantsVisit = router.query.visit === '1'
        const validModel = typeof requested === 'string'
            && (requested === CUSTOM_MODEL || requested === UNSURE_MODEL || models.some(model => model.slug === requested))

        if (!validModel && !wantsVisit) return

        setValues(current => ({
            ...current,
            model: validModel ? requested : current.model,
            message: wantsVisit && !current.message ? VISIT_MESSAGE : current.message
        }))
    }, [router.isReady, router.query.model, router.query.visit, models])

    const update = (field: FieldName) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) {
            setErrors(current => ({ ...current, [field]: undefined }))
        }
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const found = validate(values)

        if (Object.keys(found).length > 0) {
            setErrors(found)
            setStatus('error')
            setServerMessage("Please check the highlighted fields.")

            const first = Object.keys(found)[0]

            document.getElementById(`quote-${first}`)?.focus()

            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/quote', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const result = await response.json().catch(() => null) as { ok?: boolean, message?: string, fields?: FieldErrors } | null

            if (!response.ok || !result?.ok) {
                setErrors(result?.fields ?? {})
                setStatus('error')
                setServerMessage(result?.message ?? "Something went wrong. Please try again in a moment.")

                return
            }

            setStatus('success')
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        catch {
            setStatus('error')
            setServerMessage("We could not reach our server. Please check your connection and try again.")
        }
    }

    const reset = () => {
        setValues(EMPTY_FORM)
        setErrors({})
        setStatus('idle')
        setServerMessage('')
    }

    if (status === 'success') {
        return (
            <div role='status' className='rounded-[2rem] border border-border bg-card p-8 text-center shadow-[0_1px_2px_rgb(0_0_0/0.04)] sm:p-12'>
                <span className='mx-auto flex size-16 items-center justify-center rounded-full bg-primary text-primary-foreground'>
                    <CircleCheck className='size-8' aria-hidden='true' />
                </span>

                <h2 className='mt-7 font-heading text-3xl font-medium tracking-tight sm:text-4xl'>
                    Thank you, your request is on its way
                </h2>

                <p className='mx-auto mt-4 max-w-md text-lg leading-relaxed text-muted-foreground'>
                    We have received your details and will get back to you personally with ideas, prices and next steps.
                </p>

                <div className='mt-9 flex flex-wrap justify-center gap-3'>
                    <Link href='/models' className={buttonVariants({ size: 'lg' })}>
                        Browse our models
                        <ArrowRight className='size-4' />
                    </Link>

                    <Button variant='outline' size='lg' onClick={reset}>
                        Send another request
                    </Button>
                </div>
            </div>
        )
    }

    const describedBy = (field: FieldName) => (errors[field] ? `quote-${field}-error` : undefined)

    return (
        <form
            noValidate
            onSubmit={handleSubmit}
            aria-labelledby='quote-form-title'
            className='rounded-[2rem] border border-border bg-card p-6 shadow-[0_1px_2px_rgb(0_0_0/0.04)] sm:p-10'
        >
            <h2 id='quote-form-title' className='font-heading text-3xl font-medium tracking-tight'>
                Tell us about your plans
            </h2>

            <p className='mt-2 text-muted-foreground'>
                Fields marked with an asterisk are required.
            </p>

            {status === 'error' && serverMessage && (
                <div role='alert' className='mt-6 flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
                    <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    <p>{serverMessage}</p>
                </div>
            )}

            <fieldset className='mt-8'>
                <legend className='text-sm font-medium'>
                    Which model are you interested in?
                </legend>

                <div className='mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                    {models.map(model => (
                        <label
                            key={model.id}
                            className='relative flex cursor-pointer flex-col rounded-xl border border-input bg-background/60 p-4 transition-colors hover:border-primary/40 has-[input:checked]:border-primary has-[input:checked]:bg-primary/[0.06] has-[input:focus-visible]:ring-3 has-[input:focus-visible]:ring-ring/30'
                        >
                            <input
                                type='radio'
                                name='model'
                                value={model.slug}
                                checked={values.model === model.slug}
                                onChange={update('model')}
                                className='peer sr-only'
                            />

                            <span className='font-heading text-xl font-medium'>
                                <span data-mdlfy-cms={`models:${model.id}:name`}>{model.name}</span>
                            </span>

                            <span className='mt-1 flex flex-wrap gap-x-1.5 text-sm text-muted-foreground'>
                                {model.length && <span data-mdlfy-cms={`models:${model.id}:length`}>{model.length}</span>}
                                {model.length && model.price && <span aria-hidden='true'>&middot;</span>}
                                {model.price && (
                                    <span>
                                        From <span data-mdlfy-cms={`models:${model.id}:price`}>{model.price}</span>
                                    </span>
                                )}
                            </span>

                            <CircleCheck className='absolute top-4 right-4 size-5 text-primary opacity-0 transition-opacity peer-checked:opacity-100' aria-hidden='true' />
                        </label>
                    ))}

                    {[
                        { value: CUSTOM_MODEL, title: "Fully custom", text: "Designed from scratch" },
                        { value: UNSURE_MODEL, title: "Not sure yet", text: "Help me choose" }
                    ].map(option => (
                        <label
                            key={option.value}
                            className='relative flex cursor-pointer flex-col rounded-xl border border-input bg-background/60 p-4 transition-colors hover:border-primary/40 has-[input:checked]:border-primary has-[input:checked]:bg-primary/[0.06] has-[input:focus-visible]:ring-3 has-[input:focus-visible]:ring-ring/30'
                        >
                            <input
                                type='radio'
                                name='model'
                                value={option.value}
                                checked={values.model === option.value}
                                onChange={update('model')}
                                className='peer sr-only'
                            />

                            <span className='font-heading text-xl font-medium'>
                                {option.title}
                            </span>

                            <span className='mt-1 text-sm text-muted-foreground'>
                                {option.text}
                            </span>

                            <CircleCheck className='absolute top-4 right-4 size-5 text-primary opacity-0 transition-opacity peer-checked:opacity-100' aria-hidden='true' />
                        </label>
                    ))}
                </div>
            </fieldset>

            <div className='mt-8 grid gap-6 sm:grid-cols-2'>
                <div>
                    <Label htmlFor='quote-name'>
                        Your name *
                    </Label>

                    <Input
                        id='quote-name'
                        name='name'
                        autoComplete='name'
                        className='mt-2'
                        value={values.name}
                        onChange={update('name')}
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={describedBy('name')}
                        required
                    />

                    <FieldError id='quote-name-error' message={errors.name} />
                </div>

                <div>
                    <Label htmlFor='quote-email'>
                        Email address *
                    </Label>

                    <Input
                        id='quote-email'
                        name='email'
                        type='email'
                        autoComplete='email'
                        className='mt-2'
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={errors.email ? true : undefined}
                        aria-describedby={describedBy('email')}
                        required
                    />

                    <FieldError id='quote-email-error' message={errors.email} />
                </div>

                <div>
                    <Label htmlFor='quote-phone'>
                        Phone <span className='font-normal text-muted-foreground'>(optional)</span>
                    </Label>

                    <Input
                        id='quote-phone'
                        name='phone'
                        type='tel'
                        autoComplete='tel'
                        className='mt-2'
                        value={values.phone}
                        onChange={update('phone')}
                        aria-invalid={errors.phone ? true : undefined}
                        aria-describedby={describedBy('phone')}
                    />

                    <FieldError id='quote-phone-error' message={errors.phone} />
                </div>

                <div>
                    <Label htmlFor='quote-useCase'>
                        How will you use it?
                    </Label>

                    <div className='relative mt-2'>
                        <select
                            id='quote-useCase'
                            name='useCase'
                            className={cn(selectClassName, !values.useCase && 'text-muted-foreground')}
                            value={values.useCase}
                            onChange={update('useCase')}
                            aria-invalid={errors.useCase ? true : undefined}
                            aria-describedby={describedBy('useCase')}
                        >
                            <option value=''>Choose an option</option>
                            {USE_CASES.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>

                        <ChevronDown className='pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                    </div>

                    <FieldError id='quote-useCase-error' message={errors.useCase} />
                </div>

                <div>
                    <Label htmlFor='quote-timeline'>
                        When would you like it?
                    </Label>

                    <div className='relative mt-2'>
                        <select
                            id='quote-timeline'
                            name='timeline'
                            className={cn(selectClassName, !values.timeline && 'text-muted-foreground')}
                            value={values.timeline}
                            onChange={update('timeline')}
                            aria-invalid={errors.timeline ? true : undefined}
                            aria-describedby={describedBy('timeline')}
                        >
                            <option value=''>Choose an option</option>
                            {TIMELINES.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>

                        <ChevronDown className='pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                    </div>

                    <FieldError id='quote-timeline-error' message={errors.timeline} />
                </div>

                <div>
                    <Label htmlFor='quote-landStatus'>
                        Do you have land for it?
                    </Label>

                    <div className='relative mt-2'>
                        <select
                            id='quote-landStatus'
                            name='landStatus'
                            className={cn(selectClassName, !values.landStatus && 'text-muted-foreground')}
                            value={values.landStatus}
                            onChange={update('landStatus')}
                            aria-invalid={errors.landStatus ? true : undefined}
                            aria-describedby={describedBy('landStatus')}
                        >
                            <option value=''>Choose an option</option>
                            {LAND_STATUSES.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>

                        <ChevronDown className='pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                    </div>

                    <FieldError id='quote-landStatus-error' message={errors.landStatus} />
                </div>
            </div>

            <div className='mt-6'>
                <Label htmlFor='quote-message'>
                    Tell us more <span className='font-normal text-muted-foreground'>(optional)</span>
                </Label>

                <Textarea
                    id='quote-message'
                    name='message'
                    className='mt-2'
                    placeholder="Where will the house live, who will live in it, and anything you would love to have."
                    value={values.message}
                    onChange={update('message')}
                    maxLength={4000}
                    aria-invalid={errors.message ? true : undefined}
                    aria-describedby={describedBy('message')}
                />

                <FieldError id='quote-message-error' message={errors.message} />
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor='quote-website'>
                    Leave this field empty
                </label>

                <input
                    id='quote-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={update('website')}
                />
            </div>

            <div className='mt-8 flex flex-col gap-5 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-sm text-sm leading-relaxed text-muted-foreground'>
                    We only use your details to reply to your request. Read our{' '}
                    <Link href='/privacy' className='font-medium text-foreground underline underline-offset-4'>
                        privacy notice
                    </Link>
                    .
                </p>

                <Button type='submit' size='lg' disabled={status === 'submitting'} className='shrink-0'>
                    {status === 'submitting' ? (
                        <>
                            <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                            Sending
                        </>
                    ) : (
                        <>
                            Send my request
                            <ArrowRight className='size-4' />
                        </>
                    )}
                </Button>
            </div>
        </form>
    )
}
